import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => setMobileMenuOpen(false), [location.pathname]);

  const isActive = (path) =>
    path === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(path);

  const navLinks = [
    { to: "/", label: "HOME" },
    { to: "/about-us", label: "ABOUT US" },
    { to: "/products", label: "PRODUCTS" },
    { to: "/services", label: "SERVICES" },
    { to: "/news", label: "NEWS" },
    { to: "/contact", label: "CONTACT" },
  ];

  return (
    <>
      <style>{`
        .navbar-root {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 50;
          transition: background 0.35s ease, box-shadow 0.35s ease, padding 0.35s ease;
        }
        .navbar-root.at-top {
          background: rgba(61, 79, 69, 0.85);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          padding-top: 1.1rem;
          padding-bottom: 1.1rem;
          box-shadow: none;
        }
        .navbar-root.scrolled {
          background: rgba(61, 79, 69, 0.98);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          padding-top: 0.65rem;
          padding-bottom: 0.65rem;
          box-shadow: 0 2px 24px 0 rgba(30,50,40,0.18);
        }
        .nav-link {
          position: relative;
          font-size: 0.7rem;
          letter-spacing: 0.12em;
          font-weight: 300;
          color: rgba(255,255,255,0.82);
          text-decoration: none;
          padding-bottom: 3px;
          transition: color 0.2s;
        }
        .nav-link::after {
          content: "";
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 0;
          height: 1.5px;
          background: #b8c5ba;
          transition: width 0.25s ease;
        }
        .nav-link:hover { color: #b8c5ba; }
        .nav-link:hover::after { width: 100%; }
        .nav-link.active {
          color: #ffffff;
        }
        .nav-link.active::after {
          width: 100%;
          background: #ffffff;
        }
        .nav-contact-btn {
          border: 1px solid rgba(255,255,255,0.6);
          border-radius: 4px;
          padding: 6px 20px;
          font-size: 0.7rem;
          letter-spacing: 0.12em;
          color: white;
          text-decoration: none;
          transition: background 0.2s, color 0.2s, border-color 0.2s;
        }
        .nav-contact-btn:hover {
          background: white;
          color: #3d4f45;
          border-color: white;
        }
        .mobile-menu {
          background: rgba(44, 60, 50, 0.98);
          border-top: 1px solid rgba(255,255,255,0.08);
          animation: slideDown 0.22s ease;
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .mobile-nav-link {
          display: block;
          padding: 10px 24px;
          font-size: 0.72rem;
          letter-spacing: 0.1em;
          color: rgba(255,255,255,0.8);
          text-decoration: none;
          border-left: 2px solid transparent;
          transition: color 0.18s, border-color 0.18s, background 0.18s;
        }
        .mobile-nav-link:hover,
        .mobile-nav-link.active {
          color: #ffffff;
          border-left-color: #b8c5ba;
          background: rgba(255,255,255,0.04);
        }
        /* Page transition fade-in */
        .page-transition {
          animation: pageFadeIn 0.38s ease both;
        }
        @keyframes pageFadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        /* Scroll-down indicator */
        .scroll-indicator {
          position: absolute;
          bottom: 36px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          z-index: 20;
          animation: fadeInUp 1.2s ease 0.8s both;
        }
        .scroll-indicator span {
          font-size: 0.6rem;
          letter-spacing: 0.2em;
          color: rgba(255,255,255,0.7);
          text-transform: uppercase;
        }
        .scroll-chevron {
          width: 28px;
          height: 28px;
          border-right: 1.5px solid rgba(255,255,255,0.6);
          border-bottom: 1.5px solid rgba(255,255,255,0.6);
          transform: rotate(45deg);
          animation: bounce 1.6s infinite;
          margin-top: -6px;
        }
        @keyframes bounce {
          0%, 100% { transform: rotate(45deg) translateY(0); opacity: 0.6; }
          50%       { transform: rotate(45deg) translateY(6px); opacity: 1; }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateX(-50%) translateY(14px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>

      <nav className={`navbar-root px-6 ${scrolled ? "scrolled" : "at-top"}`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 no-underline">
            <img
              src="/images/logobumdes.png"
              alt="Logo BUM Desa Warga Dekat Bandung"
              className="flex-shrink-0"
              style={{ height: "36px", width: "auto" }}
            />
            <span
              style={{
                fontSize: "0.62rem",
                letterSpacing: "0.18em",
                fontWeight: 300,
                color: "white",
                lineHeight: 1.5,
              }}
            >
              BUM DESA
              <br />
              WARGA DEKAT BANDUNG
            </span>
          </Link>

          {/* Desktop links */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={`nav-link${isActive(to) ? " active" : ""}`}
              >
                {label}
              </Link>
            ))}
          </div>

          <Link to="/contact" className="nav-contact-btn hidden md:block">
            CONTACT
          </Link>

          {/* Mobile hamburger */}
          <button
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile dropdown */}
        {mobileMenuOpen && (
          <div className="mobile-menu mt-2 pb-2 rounded-b-lg">
            {navLinks.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={`mobile-nav-link${isActive(to) ? " active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {label}
              </Link>
            ))}
          </div>
        )}
      </nav>
    </>
  );
}

// Reusable scroll-down chevron — import & place inside hero sections
export function ScrollIndicator({ targetId }) {
  const handleClick = () => {
    const el = document.getElementById(targetId);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <div className="scroll-indicator" onClick={handleClick}>
      <span>Scroll</span>
      <div className="scroll-chevron" />
    </div>
  );
}
